"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import styles from "./products.module.scss";
import axios from "axios";
import { useDispatch } from "react-redux";
import { addToCart } from "@/store/cartSlice";
import Swal from "sweetalert2";
import { Settings2, X, Heart, Eye, ShoppingCart, Star } from "lucide-react";
import Loader from "../components/loader/loader";

const FeaturedProducts = dynamic(() => import("../components/productRow"));

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("All");
  const loadingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("default");
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const blockObserverRef = useRef(false);
  const categories = [
    "All",
    "Rings",
    "Earrings",
    "Necklace",
    "Bracelets",
    "Anklets",
    "Pendants",
    "Nose Pins",
    "Mangalsutra",
    "Chains",
  ];
  const serieses = [
    "All",
    "Classic",
    "Casual",
    "Engagement",
    "Vintage",
    "Bridal",
    "Luxury",
    "Everyday",
    "Custom Made",
  ];
  const sortOptions = [
    { value: "default", label: "Recommended" },
    { value: "newest", label: "Newest arrivals" },
    { value: "lowToHigh", label: "Price: low to high" },
    { value: "highToLow", label: "Price: high to low" },
    { value: "highestRating", label: "Top rated" },
  ];
  const [series, setSeries] = useState(["All"]);
  const priceRanges = [
    { label: "All", min: 0, max: Infinity },
    { label: "Under ₹20K", min: 0, max: 20000 },
    { label: "₹20K - ₹60K", min: 20000, max: 60000 },
    { label: "₹60K - ₹100K", min: 60000, max: 100000 },
    { label: "Above ₹100K", min: 100000, max: Infinity },
  ];
  const [priceRange, setPriceRange] = useState("All");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const dispatch = useDispatch();

  //observer for fetching more product if reached bottom
  let intervalId = null;
  useEffect(() => {
    if (!observerRef.current || blockObserverRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];

      let timeoutId = null;

      if (
        entry.isIntersecting &&
        hasMore &&
        !loadingRef.current &&
        !blockObserverRef.current
      ) {
        if (!timeoutId) {
          setPage((prev) => prev + 1);
          timeoutId = setTimeout(() => {
            timeoutId = null;
          }, 1000);
        }
      }
    });

    observer.observe(observerRef.current);

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
      clearInterval(intervalId);
      intervalId = null;
    };
  }, []);

  // Fetch page 1 when filters change
  useEffect(() => {
    blockObserverRef.current = true;
    setPage(1);
    setProducts([]);
    setHasMore(true);

    const selectedRange = priceRanges.find(
      (range) => range.label === priceRange,
    );

    const fetchFiltered = async () => {
      loadingRef.current = true;
      setLoading(true);
      try {
        const res = await axios.get(
          `/api/products?category=${category}&series=${series.join(
            ",",
          )}&sort=${sortOrder}&page=1&limit=6&minPrice=${
            selectedRange.min
          }&maxPrice=${selectedRange.max}&search=${searchTerm}`,
        );
        const data = await res.data;

        setTotalProducts(data.total);
        setHasMore(data.hasMore);
        setProducts(data.products);
      } catch (err) {
        console.error(
          "Failed to fetch filtered products:",
          err?.message || err,
        );
      } finally {
        loadingRef.current = false;
        setLoading(false);
        blockObserverRef.current = false;
      }
    };

    fetchFiltered();
  }, [sortOrder, category, series, priceRange, debouncedSearchTerm]);

  // Fetch more when page increases (not page 1)
  useEffect(() => {
    if (page === 1) return;

    const selectedRange = priceRanges.find(
      (range) => range.label === priceRange,
    );

    const fetchMore = async () => {
      if (loadingRef.current || !hasMore) return;

      loadingRef.current = true;
      setLoading(true);
      try {
        const res = await axios.get(
          `/api/products?category=${category}&series=${series.join(
            ",",
          )}&sort=${sortOrder}&page=${page}&limit=6&minPrice=${
            selectedRange.min
          }&maxPrice=${selectedRange.max}&search=${debouncedSearchTerm}`,
        );
        const data = await res.data;
        setProducts((prev) => [...prev, ...data.products]);
        setHasMore(data.hasMore);
      } catch (err) {
        if (err?.response?.status === 503) {
          try {
            const local = await axios.get("/api/local-products");
            const data = local.data || { products: [] };
            // stop infinite loading when fallback used
            setHasMore(false);
            if (page === 1) {
              setProducts(data.products || []);
              setTotalProducts(data.products?.length || 0);
            }
            return;
          } catch {
            // ignore
          }
        }
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    };

    fetchMore();
  }, [page]);

  const clearAllFilters = () => {
    setSearchTerm("");
    setCategory("All");
    setSeries(["All"]);
    setSortOrder("default");
    setPriceRange("All");
    setProducts([]);
    setPage(1);
    setHasMore(true);
  };

  //handle series on change
  const handleSeriesOnChange = (e, item) => {
    const { checked } = e.target;
    const value = item;

    setSeries((prev) => {
      if (value === "All") {
        return ["All"];
      } else {
        const filtered = prev.filter((s) => s !== "All");
        if (checked) {
          return [...filtered, value];
        } else {
          return filtered.filter((s) => s !== value);
        }
      }
    });
  };

  //debounce search term to prevent fetch on every click
  useEffect(() => {
    // console.log("hitted search term =", searchTerm);
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  //calculator for offers
  const calculateDiscount = (newPrice, price) => {
    return Math.round(((price - newPrice) / price) * 100);
  };

  const handleToggleWishlist = (product) => {
    Swal.fire({
      icon: "success",
      title: "Wishlist updated",
      text: `${product.name} was added to your wishlist.`,
      timer: 1000,
      showConfirmButton: false,
    });
  };

  const handleQuickView = (product) => {
    Swal.fire({
      title: product.name,
      text: product.description || "Inspect this product in detail.",
      imageUrl: product.imageUrl || "/image/imageFail.jpg",
      imageAlt: product.name,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Close",
    });
  };

  const handleAddToCart = (product) => {
    if (product.inStock === false) return;

    dispatch(addToCart({ ...product, id: product._id || product.id }));
    Swal.fire({
      icon: "success",
      title: "Added to cart",
      text: `${product.name} was added to your cart.`,
      timer: 1000,
      showConfirmButton: false,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />

        <div className={styles.categoryNav}>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`${styles.categoryItem} ${
                category === cat ? styles.active : ""
              }`}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className={styles.filterControl}>
          <span>
            {totalProducts} product{totalProducts !== 1 && "s"} found
          </span>
          <div className={styles.filterButtons}>
            <button onClick={clearAllFilters} className={styles.clearFilters}>
              Reset Filters <Settings2 size={18} />
            </button>
            <button
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              className={styles.toggleSidebar}
            >
              Filter
            </button>
          </div>
        </div>
      </div>

      <div className={styles.layout}>
        <aside
          className={`${styles.sidebar} ${isSidebarOpen && styles.openSidebar}`}
          aria-label="Product filters"
        >
          <button
            className={styles.sidebarClose}
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close filters"
          >
            <X size={18} />
          </button>
          <div className={styles.filterGroup}>
            <h4>Sort by</h4>
            {sortOptions.map(({ value, label }) => (
              <label key={value}>
                <input
                  type="radio"
                  name="sortOrder"
                  value={value}
                  checked={sortOrder === value}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <div className={styles.filterGroup}>
            <h4>Series</h4>
            {serieses.map((item) => (
              <label key={item}>
                <input
                  type="checkbox"
                  checked={series.includes(item)}
                  onChange={(e) => handleSeriesOnChange(e, item)}
                />
                {item}
              </label>
            ))}
          </div>
          <div className={styles.filterGroup}>
            <h4>Price Range</h4>
            {priceRanges.map((range) => (
              <label key={range.label}>
                <input
                  type="radio"
                  name="priceRange"
                  checked={priceRange === range.label}
                  onChange={() => setPriceRange(range.label)}
                />
                {range.label}
              </label>
            ))}
          </div>
        </aside>
        {isSidebarOpen && (
          <button
            className={styles.filterBackdrop}
            type="button"
            aria-label="Close filters"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <section className={styles.mainContent}>
          <div className={styles.productsGrid}>
            {products.length === 0 ? (
              <div className={styles.noMatch}>No products found.</div>
            ) : (
              products.map((product, idx) => {
                const hasDiscount =
                  product.newPrice && product.newPrice < product.price;
                const discountPercentage = hasDiscount
                  ? calculateDiscount(product.newPrice, product.price)
                  : 0;
                return (
                  <Link
                    key={`${product._id || product.slug || idx}-${idx}`}
                    href={`/products/${product._id || product.slug || encodeURIComponent(product.name)}`}
                    className={styles.productCard}
                  >
                    <div className={styles.imageContainer}>
                      {hasDiscount && (
                        <div className={styles.offerBadge}>
                          {discountPercentage}% OFF
                        </div>
                      )}
                      <Image
                        src={product.imageUrl || "/image/imageFail.jpg"}
                        alt={product.name}
                        width={400}
                        height={400}
                      />
                      <div className={styles.hoverActions}>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleToggleWishlist(product)}
                          aria-label="Add to wishlist"
                        >
                          <Heart size={18} />
                        </button>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleQuickView(product)}
                          aria-label="Quick view"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                      <button
                        className={`${styles.quickAddButton} ${
                          product.inStock !== false ? "" : styles.disabled
                        }`}
                        onClick={() =>
                          product.inStock && handleAddToCart(product)
                        }
                        disabled={!product.inStock}
                      >
                        <ShoppingCart size={16} />
                        {product.inStock !== false
                          ? "Add to Cart"
                          : "Out of Stock"}
                      </button>
                    </div>

                    <div className={styles.productInfo}>
                      <p className={styles.pName}>{product.name}</p>
                      <p className={styles.detail}>{product.category}</p>
                      <span className={styles.series}>{product.series}</span>
                      <div className={styles.rating}>
                        <div className={styles.stars}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              className={
                                i < Math.floor(product.rating)
                                  ? styles.starFilled
                                  : i < product.rating
                                    ? styles.starHalf
                                    : styles.starEmpty
                              }
                            />
                          ))}
                        </div>
                        <span className={styles.reviewCount}>
                          ({product.reviewCount})
                        </span>
                      </div>
                      <div className={styles.productPrice}>
                        <span className={styles.newPrice}>
                          MRP : ₹{" "}
                          {hasDiscount ? product.newPrice : product.price}
                        </span>

                        {hasDiscount && (
                          <span className={styles.oldPrice}>
                            {product.price}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
          <div ref={observerRef} />
          {loading && hasMore && <Loader />}
          <div className={styles.moreSimiler}>
            <h4 className={styles.heading}>Related Products</h4>
            <FeaturedProducts />
          </div>
        </section>
      </div>
    </div>
  );
}
